import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import type { DataTableColumn } from '@ui-kit';
import { useRoleList } from '../hooks/useRoleList';
import { useCreateRole } from '../hooks/useCreateRole';
import { RoleCreateDialog } from '../components/RoleCreateDialog';
import { SystemRoleBadge } from '../components/SystemRoleBadge';
import type { RoleListItem, RoleSearchCondition, ApiError } from '../types/role';
import type { RoleCreateFormValues } from '../schema/roleSchema';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';

const DEFAULT_PAGE_SIZE = 20;

/** 5행 Skeleton 행 렌더러 */
function SkeletonRow() {
  return (
    <tr>
      {[50, 70, 60, 40, 30, 30, 50, 60].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 14,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function RoleListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // 검색/필터 state
  const [keyword, setKeyword] = useState('');
  const [systemRoleFilter, setSystemRoleFilter] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(0);
  const [sort] = useState('priority,asc');

  // 적용된 필터 (검색 버튼 클릭 시 반영)
  const [appliedFilters, setAppliedFilters] = useState<RoleSearchCondition>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: 'priority,asc',
  });

  // 다이얼로그 state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>();

  // R9-F-005: delete success toast — read from navigation state
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string | undefined>();
  useEffect(() => {
    const state = location.state as { deletedRoleCode?: string } | null;
    if (state?.deletedRoleCode) {
      setDeleteSuccessMessage(
        t('role.toast.deleted') + ` (${state.deletedRoleCode})`
      );
      // clear history state to avoid re-showing on back-navigation
      window.history.replaceState({}, '');
      const timer = setTimeout(() => setDeleteSuccessMessage(undefined), 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.state, t]);

  // RBAC: ROLE_WRITE 보유 여부 — ButtonGuard 와 동일한 훅 재사용
  const canEdit = useHasPermission('ROLE_WRITE');

  const listQuery = useRoleList(appliedFilters);
  const createMutation = useCreateRole();

  const pageData = listQuery.data;

  const handleSearch = useCallback(() => {
    const filters: RoleSearchCondition = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort,
    };
    if (keyword.trim()) filters.keyword = keyword.trim();
    if (systemRoleFilter !== '') filters.systemRole = systemRoleFilter === 'true';
    setPage(0);
    setAppliedFilters(filters);
  }, [keyword, systemRoleFilter, sort]);

  const handleReset = useCallback(() => {
    setKeyword('');
    setSystemRoleFilter('');
    setPage(0);
    setAppliedFilters({ page: 0, size: DEFAULT_PAGE_SIZE, sort: 'priority,asc' });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleCreateSubmit = useCallback(
    (values: RoleCreateFormValues) => {
      setCreateError(undefined);
      createMutation.mutate(
        {
          code: values.code,
          nameKo: values.nameKo,
          nameEn: values.nameEn,
          description: values.description,
          priority: values.priority,
        },
        {
          onSuccess: (data) => {
            setCreateDialogOpen(false);
            navigate(`/roles/${data.id}`);
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            setCreateError(apiErr.message ?? t('role.error.network'));
          },
        }
      );
    },
    [createMutation, navigate]
  );

  const columns: DataTableColumn<RoleListItem>[] = [
    {
      key: 'code',
      header: t('role.list.column.code'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-primary)',
          }}
        >
          {row.code}
        </span>
      ),
    },
    {
      key: 'nameKo',
      header: t('role.list.column.nameKo'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
          {row.nameKo}
        </span>
      ),
    },
    {
      key: 'nameEn',
      header: t('role.list.column.nameEn'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {row.nameEn || '-'}
        </span>
      ),
    },
    {
      key: 'systemRole',
      header: t('role.list.column.systemRole'),
      render: (row) => <SystemRoleBadge systemRole={row.systemRole} />,
    },
    {
      key: 'permissionCount',
      header: t('role.list.column.permissions'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          {row.permissionCount}{t('role.list.column.permissions.unit', { defaultValue: '개' })}
        </span>
      ),
    },
    {
      key: 'userCount',
      header: t('role.list.column.users'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          {row.userCount}{t('role.list.column.users.unit', { defaultValue: '명' })}
        </span>
      ),
    },
    {
      key: 'priority',
      header: t('role.list.column.priority'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {row.priority}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('role.list.column.status', { defaultValue: '상태' }),
      render: (row) =>
        row.systemRole ? (
          <StatusPill tone="warning">{t('role.status.system')}</StatusPill>
        ) : (
          <StatusPill tone="success">{t('role.status.active', { defaultValue: '활성' })}</StatusPill>
        ),
    },
    {
      key: 'createdAt',
      header: t('role.list.column.createdAt'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {formatDate(row.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('role.list.column.actions'),
      width: '80px',
      render: (row) => (
        <button
          type="button"
          aria-label={`${row.nameKo} ${t('role.list.action.detail', { defaultValue: '상세 보기' })}`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/roles/${row.id}`);
          }}
          style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('role.list.action.detail', { defaultValue: '상세' })}
        </button>
      ),
    },
  ];

  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && pageData?.empty;
  const showTable = !isListLoading && !isListError && !isEmpty;

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnSecondary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
  };

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('role.list.title')}
        subtitle={t('roleManagement.pageHeader.subtitle')}
        breadcrumb={<span>{t('role.list.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            {/* 읽기 전용 배지: ROLE_WRITE 미보유 사용자에게 조회 전용임을 명시 */}
            {!canEdit && (
              <span
                title={t('role.readonly.tooltip')}
                aria-label={t('role.readonly.ariaLabel')}
              >
                <StatusPill tone="neutral">{t('role.readonly.badge')}</StatusPill>
              </span>
            )}
            {/* ButtonGuard: MANAGER lacks ROLE_WRITE → disabled per #324 */}
            <ButtonGuard requirePermission="ROLE_WRITE">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    setCreateError(undefined);
                    setCreateDialogOpen(true);
                  }}
                  disabled={disabled}
                  title={reason}
                  style={{
                    ...btnPrimary,
                    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  aria-label={t('role.list.button.create')}
                  aria-disabled={disabled}
                >
                  {t('role.list.button.create')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* R9-F-005: delete success toast */}
      {deleteSuccessMessage && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-success)',
          }}
        >
          {deleteSuccessMessage}
        </div>
      )}

      {/* 통계 요약 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {t('role.list.subtitle', { total: pageData.totalElements })}
        </p>
      )}

      {/* 검색/필터 바 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 200px auto',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'end',
        }}
        role="search"
        aria-label={t('role.list.search.ariaLabel', { defaultValue: '역할 검색 필터' })}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="role-search-keyword"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('role.list.filter.search')}
          </label>
          <input
            id="role-search-keyword"
            type="text"
            placeholder={t('role.list.search.placeholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="role-filter-systemRole"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('role.list.column.systemRole')}
          </label>
          <select
            id="role-filter-systemRole"
            value={systemRoleFilter}
            onChange={(e) => setSystemRoleFilter(e.target.value as '' | 'true' | 'false')}
            style={inputStyle}
          >
            <option value="">{t('role.list.filter.all')}</option>
            <option value="true">{t('role.list.filter.system')}</option>
            <option value="false">{t('role.list.filter.custom')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button type="button" onClick={handleSearch} style={btnPrimary}>
            {t('role.list.filter.search')}
          </button>
          <button type="button" onClick={handleReset} style={btnSecondary}>
            {t('role.list.filter.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading — skeleton */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('role.list.loading.ariaLabel', { defaultValue: '역할 목록 로딩 중' })}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[
                  t('role.list.column.code'),
                  t('role.list.column.nameKo'),
                  t('role.list.column.nameEn'),
                  t('role.list.column.systemRole'),
                  t('role.list.column.permissions'),
                  t('role.list.column.users'),
                  t('role.list.column.priority'),
                  t('role.list.column.status', { defaultValue: '상태' }),
                  t('role.list.column.createdAt'),
                  '',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('role.error.loadFailed', { defaultValue: '데이터를 불러오지 못했습니다' })}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('role.error.AUTH-403-001')
                  : ((listQuery.error as unknown as ApiError)?.message ??
                    t('role.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('role.list.empty.title')}
              description={t('role.list.empty.description')}
              action={
                <ButtonGuard requirePermission="ROLE_WRITE">
                  {({ disabled, reason }) => (
                    <button
                      type="button"
                      onClick={() => {
                        if (disabled) return;
                        setCreateError(undefined);
                        setCreateDialogOpen(true);
                      }}
                      disabled={disabled}
                      title={reason}
                      style={{
                        ...btnPrimary,
                        ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                      }}
                    >
                      {t('role.list.button.create')}
                    </button>
                  )}
                </ButtonGuard>
              }
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            <div role="region" aria-label={t('role.list.region.ariaLabel', { defaultValue: '역할 목록' })} style={{ overflowX: 'auto' }}>
              <DataTable
                columns={columns}
                rows={pageData.content}
                rowKey={(row) => row.id}
              />
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
              }}
            >
              <span
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
              >
                {t('role.pagination.info', {
                  total: pageData.totalElements,
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav aria-label={t('role.pagination.nav', { defaultValue: '페이지 탐색' })} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('role.pagination.prev')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('role.pagination.page', { page: i + 1, defaultValue: `${i + 1}페이지` })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-elevated)',
                      border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                      color:
                        i === pageData.number
                          ? 'var(--color-text-inverse)'
                          : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('role.pagination.next')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* Create Dialog */}
      <RoleCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        errorMessage={createError}
      />
    </div>
  );
}
